import React, { useEffect, useState } from "react";
import { SeatingBadge, vipFeeText } from "@/components/Seating";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FirstEventCelebration } from "@/components/FirstEventCelebration";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Lock, Unlock, EyeOff, UserPlus, Ban, Clock, MoreVertical, CalendarDays } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui-kit";
import { ExternalBookingDialog } from "@/components/ExternalBookingDialog";
import { ReasonDialog } from "@/components/BookingStatus";
import { PageTitle } from "@/components/SettingsPanel";
import { StatusBadge, PriceTag, evTitle } from "@/components/EventCard";
import { api, errorText } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { fmtDateTime } from "@/lib/time";
import { EventQrPosterButton } from "@/components/QrPoster";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { AppEvent, User } from "@/types";
import { useLive } from "@/lib/live";

const mi = "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer rounded-lg";

export default function VenueEvents() {
  const location = useLocation();
  const nav = useNavigate();
  const celebrate = (location.state as { celebrate?: AppEvent } | null)?.celebrate;
  const { t, lang } = usePrefs();
  const me = useAuth().user as User;
  const owner = me.venue_role === "owner" && me.venue_account?.status === "approved";
  const [list, setList] = useState<AppEvent[] | null>(null);
  const [external, setExternal] = useState<AppEvent | null>(null);
  const [cancelling, setCancelling] = useState<AppEvent | null>(null);
  const desk = ["owner", "reception"].includes(me.venue_role || "") && me.venue_account?.status === "approved";
  const cancelEvent = async (reason: string) => {
    try {
      await api.post(`/venue/events/${cancelling!.event_id}/cancel`, { reason });
      toast.success(t("b5.eventCancelled"));
      setCancelling(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t));
    }
  };
  const load = () => api.get("/venue/events").then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => {
    load();
  }, []);
  const toggle = async (e: AppEvent) => {
    try {
      await api.post(`/venue/events/${e.event_id}/requests`, { open: e.request_status !== "open" });
      load();
    } catch (err) {
      toast.error(errorText(err, t));
    }
  };

  return (
    <div data-testid="venue-events-page">
      {celebrate && <FirstEventCelebration e={celebrate} onClose={() => nav(".", { replace: true, state: null })} />}
      <PageTitle title={t("ve.title")} sub={t("ve.sub")} action={owner && (
        <Link to="/venue/events/new" data-testid="new-event-button" className="h-11 px-5 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center gap-2"><Plus className="h-4 w-4" />{t("ve.new")}</Link>
      )} />
      <div className="grid gap-2 lg:grid-cols-2 items-start">
        {list?.map((e) => (
          <div key={e.event_id} data-testid={`venue-event-${e.event_id}`} className="rounded-2xl border bg-card p-2.5 space-y-2 min-w-0">
            <div className="flex gap-3 min-w-0">
              {e.poster || e.venue.image ? <img src={imgSrc(e.poster || e.venue.image || "")} alt="" className="h-20 w-16 shrink-0 rounded-xl object-cover" />
                : <span className="h-20 w-16 shrink-0 rounded-xl bg-primary/10 grid place-items-center"><CalendarDays className="h-5 w-5 text-primary" /></span>}
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  {e.status === "hidden" ? <span className="shrink-0 rounded-full bg-zinc-700 text-white px-2 py-0.5 text-[11px] font-bold inline-flex items-center gap-1"><EyeOff className="h-3 w-3" />{t("ve.hiddenByAdmin")}</span> : <StatusBadge status={e.request_status} />}
                  {e.booking_mode === "single" && <span data-testid={`event-single-${e.event_id}`} className="shrink-0 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 px-2 py-0.5 text-[10px] font-bold">{t("bm.singleBadge")}</span>}
                  <span className="text-[11px] text-muted-foreground truncate">{lang === "ar" ? e.type_name_ar : e.type_name_en}</span>
                </div>
                <p className="font-bold truncate">{evTitle(e, lang)}</p>
                <p className="text-xs text-muted-foreground truncate">{fmtDateTime(e.start_at, lang)}</p>
                <p className="text-xs font-bold truncate"><PriceTag e={e} />{e.vip_fee && e.seating_types?.includes("vip") && <span className="font-normal text-muted-foreground" dir="auto"> · VIP {vipFeeText(e.vip_fee, e.vip_fee_per, t)}</span>}</p>
                {!!e.seating_types?.length && <div className="flex flex-wrap items-center gap-1" data-testid={`venue-event-seating-${e.event_id}`}>{e.seating_types.map((x) => <SeatingBadge key={x} s={x} />)}</div>}
              </div>
              {owner && (
                <DropdownMenu dir={lang === "ar" ? "rtl" : "ltr"}>
                  <DropdownMenuTrigger asChild>
                    <button aria-label={t("reg.more")} data-testid={`event-more-${e.event_id}`} className="h-8 w-8 shrink-0 grid place-items-center rounded-full border hover:bg-accent"><MoreVertical className="h-4 w-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[11rem] rounded-xl p-1">
                    {e.status === "published" && e.request_status !== "ended" && <DropdownMenuItem onSelect={() => nav(`/venue/events/${e.event_id}/edit`)} data-testid={`edit-event-${e.event_id}`} className={mi}><Pencil className="h-4 w-4" />{t("ve.edit")}</DropdownMenuItem>}
                    <DropdownMenuItem onSelect={() => nav(`/venue/events/new?copy=${e.event_id}`)} data-testid={`copy-event-${e.event_id}`} className={mi}><Copy className="h-4 w-4" />{t("ve.copy")}</DropdownMenuItem>
                    {e.status === "published" && e.request_status !== "ended" && <DropdownMenuItem onSelect={() => setCancelling(e)} data-testid={`cancel-event-${e.event_id}`} className={`${mi} text-primary`}><Ban className="h-4 w-4" />{t("b5.cancelEvent")}</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {e.request_status === "open" && e.requests_close_at && (
              <p data-testid={`event-auto-close-${e.event_id}`} className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
                <Clock className="h-3 w-3 shrink-0" />{t("ve.autoCloseAt").replace("{when}", fmtDateTime(e.requests_close_at, lang))}
              </p>
            )}
            {desk && e.status === "published" && !["ended", "cancelled"].includes(e.request_status) && (
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => toggle(e)} data-testid={`toggle-requests-${e.event_id}`} className={`h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 ${e.request_status === "open" ? "border" : "bg-emerald-600 text-white"}`}>
                  {e.request_status === "open" ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}{t(e.request_status === "open" ? "ve.close" : "ve.open")}
                </button>
                <button onClick={() => setExternal(e)} data-testid={`external-booking-${e.event_id}`} className="h-8 px-3 rounded-full bg-accent text-xs font-bold inline-flex items-center gap-1"><UserPlus className="h-3 w-3" />{t("b5.external")}</button>
                {e.request_status === "open" && <EventQrPosterButton e={e} />}
              </div>
            )}
          </div>
        ))}
      </div>
      {external && <ExternalBookingDialog e={external} onClose={() => setExternal(null)} />}
      {cancelling && <ReasonDialog testid="cancel-event-dialog" title={t("b5.cancelEvent")} note={t("b5.cancelEventNote")} onClose={() => setCancelling(null)} onSubmit={cancelEvent} />}
    </div>
  );
}
